import { TFunction } from 'i18next'

import { useTypedSelector } from '../..'
import moment from '../../lib/moment-fi'
import { isCommuterUser } from '../../lib/roleUtils'
import { Task, TaskDetailsInput } from '../../types'
import { Text } from './TaskDetails'
import { TrainEquipmentPunctuality } from './TrainEquipmentPunctuality'
import { useTaskEquipmentPunctualityFetch } from './useTaskEquipmentPunctualityFetch'

type Props = {
  details: TaskDetailsInput
  t: TFunction
  task: Task
}

type DetailsTrain = TaskDetailsInput['trains'][0]

type DetailsTrainParsed = DetailsTrain & { operatingDate: string; timestamp: string }

const parseTrains = (trains: DetailsTrain[], task: Task): DetailsTrainParsed[] => {
  const taskStart = moment(task.taskStartDateTime)
  const taskDate = taskStart.format('YYYY-MM-DD')

  return trains.map((train) => {
    let trainMoment = taskStart
    if (train.time) {
      trainMoment = moment(taskDate + ' ' + train.time)
      if (taskStart.isBefore(trainMoment)) {
        trainMoment.subtract(1, 'day')
      }
    }

    return {
      ...train,
      operatingDate: trainMoment.format('YYYY-MM-DD'),
      timestamp: trainMoment.toISOString(),
    }
  })
}

const getTrainsForDisplay = (
  trains: DetailsTrainParsed[],
  details: TaskDetailsInput,
  t: TFunction
): (DetailsTrainParsed & { text: string })[] => {
  const { type, isCommuter } = details

  if (trains.length === 1) {
    return [
      {
        ...trains[0],
        text: `${t(`${type}Train`)} ${trains[0].lineId || ''} ${trains[0].trainNumber}${
          trains[0].time ? `, ${trains[0].time}` : ''
        }`,
      },
    ]
  }

  if (isCommuter) {
    return trains.map((tr) => {
      return {
        ...tr,
        text: `${t('frame')} ${tr.frame || ''} ${t(`${type}Train`).toLowerCase()} ${
          tr.lineId || ''
        } ${tr.trainNumber}, ${tr.time || ''}`,
      }
    })
  }

  return []
}

export const TaskDetailsTrains = ({ details, t, task }: Props) => {
  const { type, trains } = details
  const user = useTypedSelector((state) => state.user)
  const showContent = isCommuterUser(user)

  const trainsParsed = showContent ? parseTrains(trains, task) : []

  useTaskEquipmentPunctualityFetch(
    type === 'from' && showContent,
    trainsParsed,
    task.taskStartDateTime
  )

  if (!trainsParsed.length) {
    return null
  }

  const trainsForDisplay = getTrainsForDisplay(trainsParsed, details, t)

  return (
    <>
      {trainsForDisplay?.length &&
        trainsForDisplay.map((train) => (
          <Text type={type} key={`${train.trainNumber}-${train.frame}`}>
            {train.text} {type === 'from' && <TrainEquipmentPunctuality {...train} />}
          </Text>
        ))}
    </>
  )
}
